// ============================================================
// HOOK — useScanInvoice
// ============================================================
// Orchestrates the full scan pipeline:
//   File selected → Backend upload → AI extraction → Supabase records

import { useState, useCallback } from "react";
import { uploadInvoicesForProcessing } from "../services/uploadService";
import { ScanSession } from "../types";
import { useBusiness } from "../contexts/BusinessContext";
import { useNotifications } from "../contexts/NotificationContext";

type ScanStep = "idle" | "uploading" | "queued" | "complete" | "error";

export function useScanInvoice() {
  const { activeBusiness } = useBusiness();
  const { addNotification, watchForInvoices } = useNotifications();
  const [step, setStep] = useState<ScanStep>("idle");
  const [progress, setProgress] = useState(0);
  const [session, setSession] = useState<ScanSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scanInvoices = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    if (!activeBusiness) {
      setError("Choose a business before uploading invoices.");
      setStep("error");
      return;
    }

    setError(null);
    const newSession: ScanSession = {
      id: `session_${Date.now()}`,
      startedAt: new Date().toISOString(),
      invoiceCount: files.length,
      successCount: 0,
      failedCount: 0,
      status: "scanning",
      currentStep: "Preparing upload...",
    };
    setSession(newSession);

    try {
      // Step 1: Send the file to the backend integration endpoint
      setStep("uploading");
      setProgress(35);
      updateSession(newSession, `Uploading ${files.length} invoice${files.length !== 1 ? "s" : ""}...`, "uploading");

      const uploadSince = new Date().toISOString();
      const uploadResult = await uploadInvoicesForProcessing(files, activeBusiness.id);
      if (!uploadResult.success) {
        const failedNames = uploadResult.results
          .filter((result) => !result.success)
          .map((result) => result.fileName)
          .join(", ");
        throw new Error(failedNames ? `Some invoices failed: ${failedNames}` : "Invoice upload failed");
      }

      // Files are stored — extraction and external sync happen in background.
      // NotificationContext will push updates via Supabase Realtime as each invoice completes.
      const count = uploadResult.submittedCount;
      setProgress(100);
      setStep("queued");
      setSession({
        ...newSession,
        completedAt: new Date().toISOString(),
        successCount: count,
        failedCount: uploadResult.failedCount,
        status: "complete",
        currentStep: count === 1
          ? "Invoice received — extracting in background."
          : `${count} invoices received — extracting in background.`,
        storageDestinations: [],
        storageErrors: [],
      });
      setStep("complete");

      watchForInvoices(uploadSince, count);

      if (uploadResult.failedCount > 0) {
        addNotification({
          category: "error",
          title: `${uploadResult.failedCount} upload${uploadResult.failedCount > 1 ? "s" : ""} failed`,
          body: `${uploadResult.failedCount} file${uploadResult.failedCount > 1 ? "s" : ""} could not be stored. Check your connection and try again.`,
          errorCount: uploadResult.failedCount,
        });
      }
    } catch (err: any) {
      setStep("error");
      setError(err.message);
      setSession({ ...newSession, status: "failed", failedCount: 1, currentStep: "Failed" });

      addNotification({
        category: "error",
        title: "Invoice upload failed",
        body: err.message ?? "An unexpected error occurred while uploading. Please try again.",
        errorCount: 1,
      });
    }
  }, [activeBusiness, addNotification, watchForInvoices]);

  const scanInvoice = useCallback((file: File) => scanInvoices([file]), [scanInvoices]);

  function updateSession(session: ScanSession, currentStep: string, status: ScanSession["status"]) {
    setSession((prev) => (prev ? { ...prev, currentStep, status } : prev));
  }

  const reset = useCallback(() => {
    setStep("idle");
    setProgress(0);
    setSession(null);
    setError(null);
  }, []);

  return { step, progress, session, error, scanInvoice, scanInvoices, reset };
}
